import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBrandMoney } from '../../../../shared/utils/brandCurrency';
import {
  calculateSaudiPayrollSummary,
  isSaudiNational,
  resolveEmployeeGrossSalary,
  DEFAULT_OVERTIME_RATE,
  SAUDI_MONTHLY_HOURS_DIVISOR,
} from '../../utils/payrollSaudiFormat';

const thBase =
  'px-3 py-2.5 text-xs font-semibold whitespace-nowrap border-b border-[rgba(110,155,110,0.18)] saudi-payroll-th';
const tdBase =
  'px-3 py-3 text-sm whitespace-nowrap border-b border-[rgba(110,155,110,0.1)] saudi-payroll-td';
const moneyTd = `${tdBase} tabular-nums text-end`;
const sectionEnd = 'border-r-2 border-[rgba(110,155,110,0.35)]';

function findAttendance(pointageData, employee) {
  return pointageData.find(
    (p) => p.employeeId === employee.id || p.employeeId === employee.mat
  );
}

function findAdvances(avancesRetenues, employee) {
  return avancesRetenues.find(
    (a) => a.employeeId === employee.id || a.employeeId === employee.mat
  );
}

function sumAdvanceDeductions(avanceData) {
  if (!avanceData) return 0;
  return (
    (avanceData.avanceSalaire || 0) +
    (avanceData.retenueAcompte || 0) +
    (avanceData.retenuePret || 0) +
    (avanceData.retenueOppositionSalaire || 0) +
    (avanceData.retenuePensionAlimentaire || 0) +
    (avanceData.retenueTropPercu || 0)
  );
}

function employeeBonuses(employee) {
  return (
    (employee.primeResponsabilite || 0) +
    (employee.primeRendementIndividuel || 0) +
    (employee.primeRendementCollectif || 0)
  );
}

export function buildSaudiPayrollRows(employees, { settings = {}, pointageData = [], avancesRetenues = [] } = {}) {
  return employees.map((employee, index) => {
    const attendance = findAttendance(pointageData, employee);
    const avanceData = findAdvances(avancesRetenues, employee);
    const basic = parseFloat(employee.salaireBase || employee.basicSalary || employee.salary || 0) || 0;
    const otHours = attendance?.nbrHeuresSupplementaires || 0;
    const hourlyRate = basic / SAUDI_MONTHLY_HOURS_DIVISOR;
    const overtimePay = Math.round(otHours * hourlyRate * DEFAULT_OVERTIME_RATE * 100) / 100;
    const otherDeductions = sumAdvanceDeductions(avanceData);
    const bonuses = employeeBonuses(employee);

    const gross = resolveEmployeeGrossSalary(employee, {
      ...settings,
      extras: { overtime: overtimePay, bonuses },
    });

    const summary = calculateSaudiPayrollSummary(employee, gross, {
      ...settings,
      otherDeductions,
      extras: { overtime: overtimePay, bonuses },
    });

    const { allowances } = summary;
    const saudi = isSaudiNational(employee);

    return {
      index: index + 1,
      employee,
      saudi,
      attendance,
      basic,
      allowances,
      bonuses,
      overtimePay,
      gross: summary.salaireBrut,
      gosiBase: summary.contributoryBase,
      gosiEmployee: summary.retenueGOSISalarie,
      gosiEmployer: summary.retenueGOSIEmployeur,
      sanedEmployee: summary.retenueSANEDSalarie,
      sanedEmployer: summary.retenueSANEDEmployeur,
      eosb: summary.provisionEOSB,
      otherDeductions,
      totalDeductions: summary.retenueGOSISalarie + summary.retenueSANEDSalarie + otherDeductions,
      netPay: summary.netAPayer,
    };
  });
}

const SaudiDetailedPayrollTable = ({
  employees,
  settings,
  pointageData = [],
  avancesRetenues = [],
}) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.salariesPage.${key}`, opts);

  const rows = useMemo(
    () => buildSaudiPayrollRows(employees, { settings, pointageData, avancesRetenues }),
    [employees, settings, pointageData, avancesRetenues]
  );

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        basic: acc.basic + row.basic,
        housing: acc.housing + row.allowances.housing,
        transport: acc.transport + row.allowances.transport,
        bonuses: acc.bonuses + row.bonuses,
        overtime: acc.overtime + row.overtimePay,
        gross: acc.gross + row.gross,
        gosiBase: acc.gosiBase + row.gosiBase,
        gosiEmployee: acc.gosiEmployee + row.gosiEmployee,
        gosiEmployer: acc.gosiEmployer + row.gosiEmployer,
        sanedEmployee: acc.sanedEmployee + row.sanedEmployee,
        sanedEmployer: acc.sanedEmployer + row.sanedEmployer,
        eosb: acc.eosb + row.eosb,
        otherDeductions: acc.otherDeductions + row.otherDeductions,
        totalDeductions: acc.totalDeductions + row.totalDeductions,
        netPay: acc.netPay + row.netPay,
      }),
      {
        basic: 0,
        housing: 0,
        transport: 0,
        bonuses: 0,
        overtime: 0,
        gross: 0,
        gosiBase: 0,
        gosiEmployee: 0,
        gosiEmployer: 0,
        sanedEmployee: 0,
        sanedEmployer: 0,
        eosb: 0,
        otherDeductions: 0,
        totalDeductions: 0,
        netPay: 0,
      }
    );
  }, [rows]);

  if (rows.length === 0) {
    return (
      <div className="py-16 text-center saudi-payroll-empty">
        {p('no_employees_payroll')}
      </div>
    );
  }

  const sectionHeader = (label, colSpan, sectionClass) => (
    <th colSpan={colSpan} className={`saudi-payroll-section-head ${sectionClass} ${sectionEnd}`}>
      {label}
    </th>
  );

  return (
    <div className="glass-table-shell saudi-payroll-table-wrap">
      <div className="glass-table-scroll overflow-x-auto">
        <table className="w-full min-w-[1400px] employees-table data-table saudi-payroll-table">
          <thead className="glass-table-head">
            <tr className="saudi-payroll-section-row">
              {sectionHeader(p('section_saudi_employee'), 7, 'saudi-section-employee')}
              {sectionHeader(p('section_saudi_salary'), 7, 'saudi-section-salary')}
              {sectionHeader(p('section_saudi_gosi'), 6, 'saudi-section-gosi')}
              {sectionHeader(p('section_saudi_deductions'), 3, 'saudi-section-deductions')}
              <th colSpan={2} className="saudi-payroll-section-head saudi-section-net">
                {p('section_saudi_net')}
              </th>
            </tr>
            <tr>
              <th className={`${thBase} saudi-sticky-col ${sectionEnd}`}>{p('col_num')}</th>
              <th className={thBase}>{p('col_mat')}</th>
              <th className={thBase}>{p('col_nom')}</th>
              <th className={thBase}>{p('col_prenom')}</th>
              <th className={thBase}>{p('col_assignment')}</th>
              <th className={thBase}>{p('field_nationality')}</th>
              <th className={`${thBase} ${sectionEnd}`}>{p('col_iban')}</th>

              <th className={`${thBase} text-end`}>{p('col_basic_salary')}</th>
              <th className={`${thBase} text-end`}>{p('col_housing_allowance')}</th>
              <th className={`${thBase} text-end`}>{p('col_transport')}</th>
              <th className={`${thBase} text-end`}>{p('col_bonuses')}</th>
              <th className={`${thBase} text-end`}>{p('col_overtime_hours')}</th>
              <th className={`${thBase} text-end font-bold`}>{p('col_gross_salary')}</th>
              <th className={`${thBase} text-center ${sectionEnd}`}>{p('col_days_worked')}</th>

              <th className={`${thBase} text-end`}>{p('col_gosi_base')}</th>
              <th className={`${thBase} text-end`}>{p('col_gosi_employee')}</th>
              <th className={`${thBase} text-end`}>{p('col_gosi_employer')}</th>
              <th className={`${thBase} text-end`}>{p('col_saned_employee')}</th>
              <th className={`${thBase} text-end`}>{p('col_saned_employer')}</th>
              <th className={`${thBase} text-end ${sectionEnd}`}>{p('col_eosb_provision')}</th>

              <th className={`${thBase} text-end`}>{p('col_advances_deductions')}</th>
              <th className={`${thBase} text-end`}>{p('col_total_deductions')}</th>
              <th className={`${thBase} ${sectionEnd}`}>{p('col_gosi_number')}</th>

              <th className={`${thBase} text-end font-bold`}>{p('col_net_pay')}</th>
              <th className={thBase}>{p('col_function')}</th>
            </tr>
          </thead>
          <tbody className="employees-table-body">
            {rows.map((row) => {
              const { employee: emp } = row;
              const iban = emp.iban || emp.numCcp || '—';
              const gosiNum = emp.numero_gosi || emp.securite_sociale || emp.gosiNumber || '—';

              return (
                <tr key={emp.id || emp.mat || row.index} className="glass-table-row">
                  <td className={`${tdBase} saudi-sticky-col font-medium ${sectionEnd}`}>{row.index}</td>
                  <td className={`${tdBase} font-mono text-xs`}>{emp.mat}</td>
                  <td className={tdBase}>{emp.nom}</td>
                  <td className={tdBase}>{emp.prenom}</td>
                  <td className={tdBase}>{emp.affectation || '—'}</td>
                  <td className={tdBase}>
                    <span className={row.saudi ? 'saudi-badge-saudi' : 'saudi-badge-expat'}>
                      {row.saudi ? p('nationality_saudi') : p('nationality_expat')}
                    </span>
                  </td>
                  <td className={`${tdBase} font-mono text-xs ${sectionEnd}`}>{iban}</td>

                  <td className={moneyTd}>{formatBrandMoney(row.basic)}</td>
                  <td className={moneyTd}>{formatBrandMoney(row.allowances.housing)}</td>
                  <td className={moneyTd}>{formatBrandMoney(row.allowances.transport)}</td>
                  <td className={moneyTd}>{formatBrandMoney(row.bonuses)}</td>
                  <td className={moneyTd}>{formatBrandMoney(row.overtimePay)}</td>
                  <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(row.gross)}</td>
                  <td className={`${tdBase} text-center ${sectionEnd}`}>
                    {row.attendance?.nbrJoursTravailles ?? '—'}
                  </td>

                  <td className={moneyTd}>{formatBrandMoney(row.gosiBase)}</td>
                  <td className={moneyTd}>{formatBrandMoney(row.gosiEmployee)}</td>
                  <td className={moneyTd}>{formatBrandMoney(row.gosiEmployer)}</td>
                  <td className={moneyTd}>{formatBrandMoney(row.sanedEmployee)}</td>
                  <td className={moneyTd}>{formatBrandMoney(row.sanedEmployer)}</td>
                  <td className={`${moneyTd} ${sectionEnd}`}>{formatBrandMoney(row.eosb)}</td>

                  <td className={moneyTd}>{formatBrandMoney(row.otherDeductions)}</td>
                  <td className={moneyTd}>{formatBrandMoney(row.totalDeductions)}</td>
                  <td className={`${tdBase} ${sectionEnd}`}>{gosiNum}</td>

                  <td className={`${moneyTd} font-bold saudi-net-cell`}>{formatBrandMoney(row.netPay)}</td>
                  <td className={tdBase}>{emp.fonction || emp.poste || '—'}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="saudi-payroll-tfoot">
            <tr>
              <td colSpan={7} className={`${tdBase} font-bold saudi-sticky-col ${sectionEnd}`}>
                {p('row_totals')}
              </td>
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.basic)}</td>
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.housing)}</td>
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.transport)}</td>
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.bonuses)}</td>
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.overtime)}</td>
              <td className={`${moneyTd} font-bold`}>{formatBrandMoney(totals.gross)}</td>
              <td className={`${tdBase} ${sectionEnd}`} />
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.gosiBase)}</td>
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.gosiEmployee)}</td>
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.gosiEmployer)}</td>
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.sanedEmployee)}</td>
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.sanedEmployer)}</td>
              <td className={`${moneyTd} font-semibold ${sectionEnd}`}>{formatBrandMoney(totals.eosb)}</td>
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.otherDeductions)}</td>
              <td className={`${moneyTd} font-semibold`}>{formatBrandMoney(totals.totalDeductions)}</td>
              <td className={`${tdBase} ${sectionEnd}`} />
              <td className={`${moneyTd} font-bold saudi-net-cell`}>{formatBrandMoney(totals.netPay)}</td>
              <td className={tdBase} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default SaudiDetailedPayrollTable;
